'use client';
import { motion } from 'framer-motion';
import siteTheme from '@/lib/theme';
import type { SectionTimeline } from '@/lib/cms';
import { ArrowDown } from 'lucide-react';

const { colors, fonts } = siteTheme;
const pad = 'clamp(4rem, 6vw, 8rem)';

export default function TimelineSection({ data }: { data: SectionTimeline }) {
  const isDark = data.background === 'dark';
  const isAlt = data.background === 'alt';
  
  const bgCol = isDark ? '#0D0D0F' : isAlt ? colors.bgAlt : colors.bg;
  const headingCol = isDark ? colors.gold : colors.navyDeep;
  const textCol = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(13,47,68,0.7)';
  const lineCol = isDark ? 'rgba(212,175,95,0.2)' : 'rgba(13,47,68,0.1)';
  const circleBg = isDark ? 'rgba(212,175,95,0.1)' : 'rgba(13,47,68,0.05)';

  return (
    <section style={{ backgroundColor: bgCol, padding: `${pad} clamp(2rem, 5vw, 6rem)` }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
          {data.script_heading && (
            <span style={{ 
              display: 'block', fontFamily: fonts.script, fontSize: 'clamp(2.4rem, 4vw, 3.2rem)', 
              color: colors.gold, marginBottom: '0.5rem', lineHeight: 1 
            }}>
              {data.script_heading}
            </span>
          )}
          {data.heading && (
            <h2 style={{ 
              fontFamily: fonts.serif, fontSize: 'clamp(2.8rem, 5vw, 4rem)', 
              color: headingCol, fontWeight: 300, lineHeight: 1.1, marginBottom: '1.2rem' 
            }}>
              {data.heading}
            </h2>
          )}
          {data.subheading && (
            <p style={{ 
              fontFamily: fonts.sans, fontSize: '1.4rem', color: textCol, 
              maxWidth: '600px', margin: '0 auto', lineHeight: 1.6 
            }}>
              {data.subheading}
            </p>
          )}
        </div>

        <div style={{ position: 'relative', maxWidth: '800px', margin: '0 auto' }}>
          {/* Vertical Line */}
          <div style={{
            position: 'absolute', top: '1rem', bottom: '1rem', left: '2rem', width: '2px', 
            background: lineCol, transform: 'translateX(-50%)',
          }} className="sm:left-1/2" />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4rem' }}>
            {data.steps.map((step, idx) => {
              const isEven = idx % 2 === 0;
              return (
                <motion.div 
                  key={step.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    position: 'relative',
                  }}
                  className={`flex-col sm:flex-row ${isEven ? 'sm:flex-row-reverse' : ''}`}
                >
                  {/* Content Box */}
                  <div className="w-full pl-16 sm:w-1/2 sm:px-12" style={{
                    textAlign: 'left',
                  }}>
                    <div className={isEven ? "sm:text-left" : "sm:text-right"}>
                      <span style={{
                        display: 'inline-block',
                        fontFamily: fonts.sans, fontSize: '1.2rem',
                        fontWeight: 600, color: colors.gold,
                        letterSpacing: '0.1em', textTransform: 'uppercase',
                        marginBottom: '0.5rem'
                      }}>
                        {step.step_number}
                      </span>
                      <h3 style={{
                        fontFamily: fonts.serif, fontSize: '2rem',
                        color: headingCol, marginBottom: '0.8rem',
                        fontWeight: 400
                      }}>
                        {step.title}
                      </h3>
                      {step.description && (
                        <p style={{
                          fontFamily: fonts.sans, fontSize: '1.3rem',
                          color: textCol, lineHeight: 1.6
                        }}>
                          {step.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Center Node */}
                  <div style={{
                    position: 'absolute', left: '2rem', top: '0', 
                    transform: 'translateX(-50%)',
                    width: '4rem', height: '4rem', borderRadius: '50%',
                    background: bgCol, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `2px solid ${colors.gold}`, zIndex: 1,
                  }} className="sm:left-1/2">
                    <div style={{
                      width: '2.8rem', height: '2.8rem', borderRadius: '50%',
                      background: circleBg, display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <span style={{ color: colors.gold, fontFamily: fonts.serif, fontSize: '1.4rem' }}>
                        {idx + 1}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
